import {
  collection,
  doc,
  addDoc,
  query,
  where,
  onSnapshot,
  updateDoc,
  deleteDoc,
  increment,
  writeBatch,
  serverTimestamp,
  QuerySnapshot,
  DocumentData,
  Unsubscribe
} from 'firebase/firestore';
import { db } from '../firebase/config';
import { MapPin, MapPinDocumentData, MapPinModel } from '../models/MapPin';
import { Business, BusinessDocumentData, BusinessModel } from '../models/Business';
import { PinType } from '../models/PinType';

export interface PinsServiceCallbacks {
  onPinsUpdate: (pins: MapPin[]) => void;
  onBusinessPinsUpdate: (businessPins: MapPin[]) => void;
  onError: (error: Error) => void;
}

export class PinsService {
  private unsubscribePins: Unsubscribe | null = null;
  private unsubscribeBusinesses: Unsubscribe | null = null;
  private callbacks: PinsServiceCallbacks;

  constructor(callbacks: PinsServiceCallbacks) {
    this.callbacks = callbacks;
  }

  startListening(showAllPins: boolean = false) {
    this.stopListening();

    // Listen to regular pins
    const pinsCollection = collection(db, 'pins');
    const pinsQuery = showAllPins ? pinsCollection : query(pinsCollection, where('isActive', '==', true));

    this.unsubscribePins = onSnapshot(
      pinsQuery,
      (snapshot: QuerySnapshot<DocumentData>) => {
        try {
          const pins: MapPin[] = snapshot.docs.map(doc => {
            const data = doc.data() as MapPinDocumentData;
            data.id = doc.id;
            return MapPinModel.fromFirestore(data);
          });
          this.callbacks.onPinsUpdate(pins);
        } catch (error) {
          this.callbacks.onError(error as Error);
        }
      },
      (error) => {
        this.callbacks.onError(error);
      }
    );

    // Listen to businesses
    const businessesCollection = collection(db, 'businesses');

    this.unsubscribeBusinesses = onSnapshot(
      businessesCollection,
      (snapshot: QuerySnapshot<DocumentData>) => {
        try {
          const businesses: Business[] = snapshot.docs.map(doc => {
            const data = doc.data() as BusinessDocumentData;
            data.id = doc.id;
            return BusinessModel.fromFirestore(data);
          });

          // Convert businesses to MapPin format
          const businessPins: MapPin[] = businesses
            .filter(business => business.isActive && business.subscriptionValidUntil > new Date())
            .map(business => {
              const pinData = BusinessModel.toMapPin(business);
              return MapPinModel.fromFirestore(pinData);
            });

          this.callbacks.onBusinessPinsUpdate(businessPins);
        } catch (error) {
          this.callbacks.onError(error as Error);
        }
      },
      (error) => {
        this.callbacks.onError(error);
      }
    );
  }

  stopListening() {
    if (this.unsubscribePins) {
      this.unsubscribePins();
      this.unsubscribePins = null;
    }
    if (this.unsubscribeBusinesses) {
      this.unsubscribeBusinesses();
      this.unsubscribeBusinesses = null;
    }
  }

  destroy() {
    this.stopListening();
  }

  // MARK: - Writes (require a signed-in user)

  // Mirrors iOS MapViewModel.addPin
  static async addPin(
    latitude: number,
    longitude: number,
    type: PinType,
    title: string,
    description: string,
    createdBy: string,
    imageURL?: string,
    hasLiveChat: boolean = false
  ): Promise<void> {
    await addDoc(collection(db, 'pins'), {
      latitude,
      longitude,
      type,
      title,
      description,
      createdBy,
      createdAt: serverTimestamp(),
      isActive: true,
      reportCount: 0,
      imageURL: imageURL ?? null,
      hasLiveChat,
      verificationYes: 0,
      verificationNo: 0
    });
  }

  // Community verification — rules allow signed-in users to update only
  // verificationYes / verificationNo / reportCount
  static async verifyPin(pinId: string, isYes: boolean): Promise<void> {
    await updateDoc(doc(db, 'pins', pinId), {
      [isYes ? 'verificationYes' : 'verificationNo']: increment(1)
    });
  }

  // Mirrors iOS reportPin — writes a report doc + atomically increments count
  static async reportPin(pinId: string, reportedBy: string, reason: string): Promise<void> {
    const pinRef = doc(db, 'pins', pinId);
    const batch = writeBatch(db);

    batch.set(doc(collection(pinRef, 'reports')), {
      reason: reason.slice(0, 500),
      reportedBy,
      reportedAt: serverTimestamp()
    });
    batch.update(pinRef, { reportCount: increment(1) });

    await batch.commit();
  }

  // Admin / creator only (rules enforce)
  static async deactivatePin(pinId: string): Promise<void> {
    await updateDoc(doc(db, 'pins', pinId), { isActive: false });
  }

  static async deletePin(pinId: string): Promise<void> {
    await deleteDoc(doc(db, 'pins', pinId));
  }
}
